import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronDown, ChevronUp, UserPlus, Phone, Mail } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getParcelRef,
  formatParcelLabel,
  type ParcelRecord,
} from '@/utils/parcelGeometry';

export interface ParcelDrawerContact {
  id?: number;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  relationship?: string | null;
}

export interface ParcelDrawerMember {
  id: number;
  name: string | null;
  ref_number: string | null;
  contacts?: ParcelDrawerContact[];
}

interface ParcelDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parcel: ParcelRecord | null;
  /** Linked members (with contacts) for the selected parcel. */
  members: ParcelDrawerMember[];
  /** Hazard tags aggregated across the parcel's linked members. */
  hazards: string[];
  /** Opens the member link/unlink dialog. When omitted, the drawer is
   *  read-only (no manage/link button) — e.g. the Members map "View" mode. */
  onManageMembers?: () => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/**
 * Bottom-sheet drawer for a tapped parcel. Replaces the old map popup so the
 * detail can grow: the collapsed view shows the essentials + a link/manage
 * action, and "More details" reveals hazards and the linked members' contacts.
 * Shared so any map (not just the admin parcels map) can reuse it.
 */
export function ParcelDetailDrawer({
  open,
  onOpenChange,
  parcel,
  members,
  hazards,
  onManageMembers,
}: ParcelDetailDrawerProps) {
  const [expanded, setExpanded] = useState(false);

  // Collapse back to the summary whenever a different parcel is opened.
  useEffect(() => {
    if (open) setExpanded(false);
  }, [open, parcel]);

  if (!parcel) return null;

  const ref = getParcelRef(parcel);
  const title = formatParcelLabel(parcel) || ref || 'Parcel';
  const street = [parcel.StreetNo, parcel.StreetName].filter(Boolean).join(' ');
  const linked = members.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[85vh] overflow-y-auto rounded-t-xl sm:max-w-xl"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {title}
            {linked && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                FPA member
              </Badge>
            )}
          </SheetTitle>
          {ref && <SheetDescription>LISKEY {ref}</SheetDescription>}
        </SheetHeader>

        <div className="mt-3 space-y-1.5">
          {parcel.TownshipFarmName && <Row label="Township">{parcel.TownshipFarmName}</Row>}
          {street && <Row label="Street">{street}</Row>}
          {parcel.shape_area != null && (
            <Row label="Area">
              {Number(parcel.shape_area).toLocaleString(undefined, { maximumFractionDigits: 4 })} ha
            </Row>
          )}
          <Row label={members.length > 1 ? 'Members' : 'Member'}>
            {linked ? (
              <span className="flex flex-col gap-0.5">
                {members.map((m) => (
                  <RouterLink
                    key={m.id}
                    to={`/members/fpa/${m.id}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {m.name || `Member #${m.id}`}
                    {m.ref_number ? ` · ${m.ref_number}` : ''}
                  </RouterLink>
                ))}
              </span>
            ) : (
              <span className="text-muted-foreground">Not linked</span>
            )}
          </Row>
        </div>

        {(hazards.length > 0 || linked) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 flex items-center gap-1 text-sm font-medium text-primary"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            {expanded ? 'Less' : 'More details'}
          </button>
        )}

        {expanded && (
          <div className="mt-3 space-y-3 border-t pt-3">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Hazards
              </div>
              {hazards.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {hazards.map((h) => (
                    <Badge key={h} variant="outline">{h}</Badge>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">None recorded</div>
              )}
            </div>

            {members.map((m) => (
              <div key={m.id}>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {m.name || `Member #${m.id}`} — contacts
                </div>
                {m.contacts && m.contacts.length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {m.contacts.map((c, i) => (
                      <li key={c.id ?? i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span>{c.name || c.relationship || 'Contact'}</span>
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-primary">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </a>
                        )}
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-primary">
                            <Mail className="h-3 w-3" /> {c.email}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-muted-foreground">No contacts on file</div>
                )}
              </div>
            ))}
          </div>
        )}

        {onManageMembers && (
          <Button className="mt-4 w-full gap-2" onClick={onManageMembers}>
            <UserPlus className="h-4 w-4" />
            {linked ? 'Manage members' : 'Link member'}
          </Button>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default ParcelDetailDrawer;
